import React, { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@apollo/client/react'
import { GET_ENABLED_FEATURE_FLAGS } from '../graphql/queries/enabledFeatureFlags'
import type { GetEnabledFeatureFlagsQuery } from '../types/graphql-generated'
import type { FeatureFlagName } from '../types/featureFlags'
import { useUserAccount } from '../hooks/useUserAccount'
import { publicApolloClient } from '../lib/apolloPublic'

export interface FeatureFlagsContextValue {
  enabledFlags: FeatureFlagName[]
  loading: boolean
  error: Error | undefined
  isEnabled: (flagName: FeatureFlagName) => boolean
}

// eslint-disable-next-line react-refresh/only-export-components
export const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined)

interface FeatureFlagsProviderProps {
  children: ReactNode
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const { userAccount, loading: userAccountLoading } = useUserAccount()
  const shouldFetchAuthFlags = isAuthenticated && !authLoading && !userAccountLoading && !!userAccount
  const [publicFlags, setPublicFlags] = useState<FeatureFlagName[]>([])
  const shouldFetchPublic = !authLoading && !isAuthenticated
  const [publicLoading, setPublicLoading] = useState(shouldFetchPublic)
  const [prevShouldFetchPublic, setPrevShouldFetchPublic] = useState(shouldFetchPublic)

  if (prevShouldFetchPublic !== shouldFetchPublic) {
    setPrevShouldFetchPublic(shouldFetchPublic)
    if (shouldFetchPublic) {
      setPublicLoading(true)
    }
  }

  // Fetch flags for unauthenticated users via the public endpoint
  useEffect(() => {
    if (authLoading || isAuthenticated) {
      return
    }
    publicApolloClient
      .query<GetEnabledFeatureFlagsQuery>({ query: GET_ENABLED_FEATURE_FLAGS })
      .then((result) => {
        setPublicFlags((result.data?.enabledFeatureFlags || []) as FeatureFlagName[])
      })
      .catch(() => {
        setPublicFlags([])
      })
      .finally(() => {
        setPublicLoading(false)
      })
  }, [isAuthenticated, authLoading])

  const { data, previousData, loading: queryLoading, error } = useQuery<GetEnabledFeatureFlagsQuery>(
    GET_ENABLED_FEATURE_FLAGS,
    {
      skip: !shouldFetchAuthFlags,
      pollInterval: 5 * 60 * 1000,
    }
  )
  const flagsData = data ?? previousData

  // Stable auth flags — only update state when the actual flag values change,
  // preventing re-renders on every poll when flags are unchanged.
  const [stableAuthFlags, setStableAuthFlags] = useState<FeatureFlagName[]>([])
  const prevAuthFlagsRef = useRef<string>('')

  useEffect(() => {
    if (!shouldFetchAuthFlags || !flagsData) {
      return
    }

    const newFlags = (flagsData.enabledFeatureFlags || []) as FeatureFlagName[]
    const newFlagsStr = JSON.stringify([...newFlags].sort())
    if (newFlagsStr !== prevAuthFlagsRef.current) {
      prevAuthFlagsRef.current = newFlagsStr
      setStableAuthFlags(newFlags)
    }
  }, [flagsData, shouldFetchAuthFlags])

  const effectivePublicFlags = (authLoading || isAuthenticated) ? [] : publicFlags
  const enabledFlags = isAuthenticated ? stableAuthFlags : effectivePublicFlags
  const authFlagsLoading = shouldFetchAuthFlags && !flagsData && queryLoading
  const authFlagsError = shouldFetchAuthFlags && !flagsData ? error : undefined

  const isEnabled = useCallback(
    (flagName: FeatureFlagName): boolean => enabledFlags.includes(flagName),
    [enabledFlags]
  )

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({ enabledFlags, loading: authLoading || authFlagsLoading || publicLoading, error: authFlagsError, isEnabled }),
    [enabledFlags, authLoading, authFlagsLoading, publicLoading, authFlagsError, isEnabled]
  )

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
}
